import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import {
  alpha,
  Backdrop,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { BASE } from 'common/src/routes/BaseRoutes';
import {
  getPasswordRules,
  isPasswordValid,
} from 'common/src/validators/passwordValidation';
import { DevFeatures } from 'common/src/types/enums/ProductFeatures.enum';

import { API_BASE_PATHS } from 'portal/src/api/constants';
import { getPasswordRulesFromOobCodeService } from 'portal/src/api/UsersService';
import useAuth from 'portal/src/auth/useAuth';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import usePageTitle from 'portal/src/navigation/usePageTitle';
import FormController from 'portal/src/components/common/forms/FormController';
import PasswordRulesDisplay from 'portal/src/components/common/PasswordRulesDisplay';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { getFields } from './ResetPassword.formFields';

import UserLoadingError from '../UserLoadingError';

export default function ResetPassword() {
  usePageTitle('Reset Password');

  const snackbarHandler = useSnackbarHandler();
  const { featureChecker } = useAuth();
  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    watch,
  } = useForm({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');
  const newPasswordIsDirty = dirtyFields.newPassword ?? false;
  const confirmPasswordIsDirty = dirtyFields.confirmPassword ?? false;
  const [loading, setLoading] = useState(false);
  const [newPassword, confirmPassword] = watch([
    'newPassword',
    'confirmPassword',
  ]);
  const {
    data: passwordRules,
    isLoading: loadingPasswordRules,
    isError: passwordRulesError,
    refetch: refetchPasswordRules,
  } = useQuery({
    queryKey: ['password-rules', oobCode],
    queryFn: () => getPasswordRulesFromOobCodeService(oobCode),
    enabled: !!oobCode,
  });
  const { valid, results: constraints } = isPasswordValid(
    newPassword,
    confirmPassword,
    passwordRules?.rules ?? getPasswordRules(),
  );

  useEffect(() => {
    if (!oobCode) {
      navigate(BASE.AUTH.LOGIN);
    }
  }, [navigate, oobCode]);

  const resetPassword = async (oobCode: string, newPassword: string) => {
    try {
      const route = featureChecker.devFeatureEnabled(DevFeatures.WorkOsLogin)
        ? API_BASE_PATHS.AUTH.WORKOS_RESET_PASSWORD
        : API_BASE_PATHS.AUTH.RESET_PASSWORD;
      const response = await fetch(`${import.meta.env.VITE_API_URL}${route}`, {
        body: JSON.stringify({
          oobCode,
          newPassword,
        }),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'post',
      });
      if (!response.ok) {
        throw new Error('unable to change the password');
      }
      snackbarHandler.success(MESSAGES.PASSWORD.CHANGED);
      navigate(BASE.AUTH.LOGIN);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.PASSWORD.CHANGE_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSumit = ({ newPassword }) => {
    if (!valid) return;

    setLoading(true);
    resetPassword(oobCode, newPassword);
  };

  if (passwordRulesError) {
    return <UserLoadingError onRefreshClick={refetchPasswordRules} />;
  }

  return (
    <Box>
      {loadingPasswordRules && (
        <Backdrop open={loadingPasswordRules}>
          <CircularProgress aria-label="Loading" />
        </Backdrop>
      )}
      <Stack spacing={2} sx={{ alignItems: 'center', marginTop: 4 }}>
        <Stack
          sx={{
            width: '4.5rem',
            height: '4.5rem',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            borderRadius: '50%',
            backgroundColor: (theme) =>
              alpha(theme.palette.secondary.main, 0.1),
          }}
        >
          <Stack
            sx={{
              width: '3rem',
              height: '3rem',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: (theme) =>
                alpha(theme.palette.secondary.main, 0.1),
            }}
          >
            <LockIcon fontSize="large" color="secondary" />
          </Stack>
        </Stack>
        <Typography variant="h3" sx={{ textAlign: 'center' }}>
          Reset your password
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit((data) => handleSumit(data))}>
        <Stack spacing={2} sx={{ alignItems: 'center', marginTop: 4 }}>
          <PasswordRulesDisplay
            constraints={constraints}
            fieldsAreDirty={newPasswordIsDirty || confirmPasswordIsDirty}
          />
          <Stack spacing={2} sx={{ width: '24rem' }}>
            {getFields().map((fieldConf, index) => (
              <FormController
                control={control}
                fieldConf={fieldConf}
                key={index}
              />
            ))}
          </Stack>
          <Button
            disabled={!valid || loading}
            type="submit"
            variant="contained"
            disableElevation
          >
            Reset Password
          </Button>
          <Button
            disabled={loading}
            variant="text"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to={BASE.AUTH.LOGIN}
          >
            Back to login
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
